import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateInput } from "../components/DateInput";
import { ModalCloseButton } from "../components/ModalCloseButton";
import { QuickEditSheet, type QuickEditValue } from "../components/QuickEditSheet";
import { api, type ApiError } from "../lib/api";
import { formatExpirationMonth } from "../lib/expiration";
import { groupItemsForBagDisplay, isAirwayBagName, isQrsJumpKitName } from "../lib/qrsJumpKitLocations";
import { sortRowsForRelatedDisplay } from "../lib/relatedRowOrdering";
import { buildRelatedRowMeta, relatedRowClass } from "../lib/relatedRows";
import { removeReasonAuditNote, TRUCK_CHECK_REMOVE_REASON_OPTIONS } from "../lib/removeReasons";
import {
  isTruckCheckTaskCurrentlyMissing,
  truckCheckDisplayExpiration,
  truckCheckDisplayQuantity,
  truckCheckStatusChipLabel
} from "../lib/truckCheckDisplay";
import { openedTruckCheckNotice } from "../lib/truckCheckNotices";
import { truckCheckVerificationPrompt } from "../lib/truckCheckPrompts";
import { canModifyTruckCheckTasks } from "../lib/truckCheckPermissions";
import {
  readTruckCheckViewState,
  selectTruckCheckBagId,
  type TruckCheckViewPatch,
  type TruckCheckViewState,
  updateTruckCheckViewSearchParams
} from "../lib/truckCheckViewState";
import { applyTruckCheckTaskVerification } from "../lib/truckCheckPatches";
import { createLatestRequestGate, type LatestRequestToken } from "../lib/latestRequest";
import type {
  Apparatus,
  StockLotChoice,
  TruckCheckDetails,
  TruckCheckHistoryRow,
  TruckCheckTask,
  TruckCheckTaskVerificationResult
} from "../lib/types";
import { useRegisterViewRefresh, useUnsavedChangesWarning } from "../lib/viewRefresh";
import { DailyTruckCheckPanel } from "./DailyTruckCheckPanel";

const DAILY_TRUCK_CHECK_VISIBLE = false;

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function fmt(value: string | null | undefined) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return value;
  return parsed.toLocaleString();
}

function initialValueForTask(task: TruckCheckTask, enteredName: string): QuickEditValue {
  const quantity = task.observedQuantity ?? task.startingQuantity ?? task.expectedQuantity ?? 0;
  return {
    enteredName,
    quantity,
    expirationDate: quantity <= 0 ? "" : task.observedExpirationDate ?? task.startingExpirationDate ?? task.expectedExpirationDate ?? "",
    notes: task.observedNotes ?? task.startingNotes ?? task.note ?? "",
    sourceType: "",
    stockLotId: "",
    stockTransferQuantity: quantity > 0 ? quantity : 1
  };
}

function truckCheckSource(value: QuickEditValue): "stock" | "other" | null {
  return value.sourceType === "stock" || value.sourceType === "other" ? value.sourceType : null;
}

function truckCheckStatusLabel(status: TruckCheckDetails["check"]["status"] | TruckCheckHistoryRow["status"]): string {
  if (status === "in_progress") return "In Progress";
  if (status === "completed") return "Completed";
  if (status === "abandoned") return "Abandoned";
  return status;
}

function truckCheckStatusClass(status: TruckCheckDetails["check"]["status"] | TruckCheckHistoryRow["status"]): string {
  if (status === "completed") return "truck-check-status-completed";
  if (status === "abandoned") return "truck-check-status-abandoned";
  return "truck-check-status-progress";
}

function truckCheckReadOnlyMessage(status: TruckCheckDetails["check"]["status"]): string {
  if (status === "completed") return "Completed report. Read-only.";
  if (status === "abandoned") return "Abandoned check. Read-only unless resumed.";
  return "";
}

function statusChip(task: TruckCheckTask, tasks: TruckCheckTask[]) {
  const label = truckCheckStatusChipLabel(task, tasks);
  if (!label) return null;
  if (label === "Deficient") return <span className="reason-chip reason-chip-red">Deficient</span>;
  return <span className="reason-chip reason-chip-ok">{label}</span>;
}

export function TruckCheckScreen(props: {
  enteredName: string;
  rememberName: (name: string) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewState = useMemo(() => readTruckCheckViewState(searchParams), [searchParams]);
  const updateViewState = useCallback(
    (patch: TruckCheckViewPatch) => {
      setSearchParams((current) => updateTruckCheckViewSearchParams(current, patch), { replace: true });
    },
    [setSearchParams]
  );

  const setMode = (mode: "daily" | "monthly") => {
    updateViewState({
      mode,
      checkId: null,
      bagId: null,
      historyView: false
    });
  };

  return (
    <div className="stack-md">
      <section className="card stack-sm">
        <div className="row-between">
          <div>
            <h2>Truck Check</h2>
            <p className="muted small">
              Complete the full monthly inventory verification for each apparatus.
            </p>
          </div>
          <div className="actions-wrap">
            {DAILY_TRUCK_CHECK_VISIBLE ? (
              <button
                className={`btn ${viewState.mode === "monthly" ? "btn-primary" : "btn-secondary"}`}
                type="button"
                onClick={() => setMode("monthly")}
              >
                Monthly
              </button>
            ) : (
              <span className="btn btn-primary truck-check-mode-label" aria-current="page">
                Monthly
              </span>
            )}
            {DAILY_TRUCK_CHECK_VISIBLE ? (
              <button
                className={`btn ${viewState.mode === "daily" ? "btn-primary" : "btn-secondary"}`}
                type="button"
                onClick={() => setMode("daily")}
              >
                Daily
              </button>
            ) : null}
          </div>
        </div>
      </section>
      {viewState.mode === "daily" ? (
        <DailyTruckCheckPanel {...props} viewState={viewState} updateViewState={updateViewState} />
      ) : (
        <MonthlyTruckCheckPanel {...props} viewState={viewState} updateViewState={updateViewState} />
      )}
    </div>
  );
}

function MonthlyTruckCheckPanel({
  enteredName,
  rememberName,
  viewState,
  updateViewState
}: {
  enteredName: string;
  rememberName: (name: string) => void;
  viewState: TruckCheckViewState;
  updateViewState: (patch: TruckCheckViewPatch) => void;
}) {
  const [apparatus, setApparatus] = useState<Apparatus[]>([]);
  const [activeCheck, setActiveCheck] = useState<TruckCheckDetails | null>(null);
  const [history, setHistory] = useState<TruckCheckHistoryRow[]>([]);
  const [historyStatus, setHistoryStatus] = useState<"all" | "in_progress" | "completed" | "abandoned">("all");
  const [loadedApparatusId, setLoadedApparatusId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  const [openNotice, setOpenNotice] = useState("");

  const [startName, setStartName] = useState(enteredName);
  const [startNote, setStartNote] = useState("");

  const [taskModal, setTaskModal] = useState<TruckCheckTask | null>(null);
  const [taskName, setTaskName] = useState(enteredName);
  const [taskExists, setTaskExists] = useState(true);
  const [taskNote, setTaskNote] = useState("");
  const [stockLots, setStockLots] = useState<StockLotChoice[]>([]);
  const [stockLotsLoading, setStockLotsLoading] = useState(false);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeName, setCompleteName] = useState(enteredName);
  const [completeDate, setCompleteDate] = useState(isoToday());
  const [completeNote, setCompleteNote] = useState("");
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [abandonName, setAbandonName] = useState(enteredName);
  const [abandonNote, setAbandonNote] = useState("");
  const [resumeCheck, setResumeCheck] = useState<TruckCheckHistoryRow | null>(null);
  const [resumeName, setResumeName] = useState(enteredName);
  const [resumeNote, setResumeNote] = useState("");
  const [deleteCheck, setDeleteCheck] = useState<TruckCheckHistoryRow | null>(null);
  const apparatusRequestsRef = useRef(createLatestRequestGate());
  const scopeRequestsRef = useRef(createLatestRequestGate());
  const historyRequestsRef = useRef(createLatestRequestGate());
  const stockLotRequestsRef = useRef(createLatestRequestGate());
  const activeCheckRef = useRef<TruckCheckDetails | null>(null);
  const selectedApparatusIdRef = useRef(viewState.apparatusId);
  const selectedApparatusId = viewState.apparatusId;
  const selectedBagId = viewState.bagId;
  const viewingHistoryCheckId = viewState.historyView ? viewState.checkId : "";
  selectedApparatusIdRef.current = selectedApparatusId;

  const isCurrentApparatusRequest = (request: LatestRequestToken, apparatusId: string) =>
    request.isCurrent() && selectedApparatusIdRef.current === apparatusId;

  const commitActiveCheck = (nextCheck: TruckCheckDetails | null) => {
    activeCheckRef.current = nextCheck;
    setActiveCheck(nextCheck);
  };

  const reconcileActiveHistoryRow = (rows: TruckCheckHistoryRow[]) => {
    const currentCheck = activeCheckRef.current;
    if (!currentCheck) return rows;
    const tasks = currentCheck.bags.flatMap((bag) => bag.tasks);
    return rows.map((row) =>
      row.id === currentCheck.check.id
        ? {
            ...row,
            totalTasks: currentCheck.progress.totalTasks,
            verifiedTasks: currentCheck.progress.verifiedTasks,
            deficientTasks: tasks.filter((task) => task.verified && task.resultStatus === "deficient").length,
            correctedTasks: tasks.filter((task) => task.verified && task.resultStatus === "corrected").length
          }
        : row
    );
  };

  const loadApparatus = async (request: LatestRequestToken) => {
    const result = await api.getApparatus({
      signal: request.signal,
      dedupe: false
    });
    if (!request.isCurrent()) return;

    setApparatus(result.apparatus);
    const currentApparatusId = selectedApparatusIdRef.current;
    const requestedApparatusExists = result.apparatus.some((row) => row.id === currentApparatusId);
    if (!requestedApparatusExists && result.apparatus.length > 0 && request.isCurrent()) {
      updateViewState({
        apparatusId: result.apparatus[0].id,
        checkId: viewState.checkId || null,
        bagId: viewState.bagId || null,
        historyView: viewState.historyView
      });
    }
  };

  const refreshForApparatus = async (apparatusId: string, request: LatestRequestToken) => {
    if (!apparatusId) return;
    let effectiveApparatusId = apparatusId;
    let nextCheck: TruckCheckDetails | null = null;
    let restoredHistoryView = false;

    if (viewState.checkId) {
      try {
        nextCheck = await api.getTruckCheck(viewState.checkId, {
          signal: request.signal,
          dedupe: false
        });
        if (!isCurrentApparatusRequest(request, apparatusId)) return;
        effectiveApparatusId = nextCheck.check.apparatusId;
        restoredHistoryView = viewState.historyView;
      } catch (err: any) {
        if (!isCurrentApparatusRequest(request, apparatusId)) return;
        if (err?.code !== "TRUCK_CHECK_NOT_FOUND") throw err;
        setOpenNotice("That saved truck check is no longer available. Showing the current check instead.");
        const active = await api.getActiveTruckCheck(apparatusId, {
          signal: request.signal,
          dedupe: false
        });
        if (!isCurrentApparatusRequest(request, apparatusId)) return;
        nextCheck = active.check;
      }
    } else {
      const active = await api.getActiveTruckCheck(apparatusId, {
        signal: request.signal,
        dedupe: false
      });
      if (!isCurrentApparatusRequest(request, apparatusId)) return;
      nextCheck = active.check;
    }

    if (!isCurrentApparatusRequest(request, apparatusId)) return;
    commitActiveCheck(nextCheck);
    setLoadedApparatusId(effectiveApparatusId);
    if (nextCheck) {
      const nextBagId = selectTruckCheckBagId(nextCheck.bags, selectedBagId);
      updateViewState({
        apparatusId: effectiveApparatusId,
        checkId: nextCheck.check.id,
        bagId: nextBagId || null,
        historyView: restoredHistoryView
      });
      if (restoredHistoryView) {
        setOpenNotice(
          openedTruckCheckNotice("monthly", nextCheck.check.apparatusName, truckCheckStatusLabel(nextCheck.check.status))
        );
      }
    } else {
      updateViewState({
        apparatusId: effectiveApparatusId,
        checkId: null,
        bagId: null,
        historyView: false
      });
    }

    return effectiveApparatusId;
  };

  const refreshHistoryForApparatus = async (apparatusId: string) => {
    const request = historyRequestsRef.current.begin();
    setHistoryLoading(true);
    try {
      const hist = await api.getTruckCheckHistory(apparatusId, historyStatus, 50, {
        signal: request.signal,
        dedupe: false
      });
      if (!isCurrentApparatusRequest(request, apparatusId)) return;
      setHistory(reconcileActiveHistoryRow(hist.rows));
    } catch (err) {
      if (!isCurrentApparatusRequest(request, apparatusId)) return;
      throw err;
    } finally {
      if (request.isCurrent()) setHistoryLoading(false);
    }
  };

  const refreshHistory = async () => {
    if (!selectedApparatusId || loadedApparatusId !== selectedApparatusId) return;
    await refreshHistoryForApparatus(selectedApparatusId);
  };

  const refreshHistoryAfterSave = async (successMessage: string) => {
    try {
      await refreshHistory();
    } catch {
      setOpenNotice(`${successMessage} History could not be refreshed; use Refresh when the connection improves.`);
    }
  };

  const afterTaskUpdate = async (updated: TruckCheckDetails) => {
    commitActiveCheck(updated);
    setLoadedApparatusId(updated.check.apparatusId);
    const nextBagId = selectTruckCheckBagId(updated.bags, selectedBagId);
    updateViewState({
      apparatusId: updated.check.apparatusId,
      checkId: updated.check.id,
      bagId: nextBagId || null,
      historyView: viewState.historyView
    });
    await refreshHistoryAfterSave("Truck check saved.");
  };

  const afterTaskVerification = (result: TruckCheckTaskVerificationResult) => {
    if (!activeCheck) return;
    const updated = applyTruckCheckTaskVerification(activeCheck, result);
    commitActiveCheck(updated);
    setLoadedApparatusId(updated.check.apparatusId);
    const nextBagId = selectTruckCheckBagId(updated.bags, selectedBagId);
    updateViewState({
      apparatusId: updated.check.apparatusId,
      checkId: updated.check.id,
      bagId: nextBagId || null,
      historyView: viewState.historyView
    });

    const tasks = updated.bags.flatMap((bag) => bag.tasks);
    setHistory((current) =>
      current.map((row) =>
        row.id === updated.check.id
          ? {
              ...row,
              totalTasks: updated.progress.totalTasks,
              verifiedTasks: updated.progress.verifiedTasks,
              deficientTasks: tasks.filter((task) => task.verified && task.resultStatus === "deficient").length,
              correctedTasks: tasks.filter((task) => task.verified && task.resultStatus === "corrected").length
            }
          : row
      )
    );
  };

  const closeTaskModal = () => {
    stockLotRequestsRef.current.cancel();
    setStockLots([]);
    setStockLotsLoading(false);
    setTaskModal(null);
  };

  useEffect(() => {
    const request = apparatusRequestsRef.current.begin();
    setLoading(true);
    loadApparatus(request)
      .catch((err: ApiError) => {
        if (request.isCurrent()) setError(err.message);
      })
      .finally(() => {
        if (request.isCurrent()) setLoading(false);
      });
    return () => apparatusRequestsRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedApparatusId) {
      scopeRequestsRef.current.cancel();
      historyRequestsRef.current.cancel();
      stockLotRequestsRef.current.cancel();
      setLoadedApparatusId("");
      commitActiveCheck(null);
      setHistory([]);
      setBusy(false);
      setHistoryLoading(false);
      return;
    }
    const request = scopeRequestsRef.current.begin();
    historyRequestsRef.current.cancel();
    stockLotRequestsRef.current.cancel();
    setOpenNotice("");
    setTaskModal(null);
    setStockLots([]);
    setStockLotsLoading(false);
    setCompleteOpen(false);
    setAbandonOpen(false);
    setResumeCheck(null);
    setDeleteCheck(null);
    setLoadedApparatusId("");
    commitActiveCheck(null);
    setHistory([]);
    setBusy(true);
    setHistoryLoading(false);
    setError("");
    refreshForApparatus(selectedApparatusId, request)
      .then((effectiveApparatusId) => {
        if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
        setBusy(false);
        if (effectiveApparatusId === selectedApparatusIdRef.current) {
          void refreshHistoryForApparatus(effectiveApparatusId).catch((err: ApiError) => {
            if (selectedApparatusIdRef.current === effectiveApparatusId) setError(err.message);
          });
        }
      })
      .catch((err: ApiError) => {
        if (isCurrentApparatusRequest(request, selectedApparatusId)) setError(err.message);
      })
      .finally(() => {
        if (isCurrentApparatusRequest(request, selectedApparatusId)) setBusy(false);
      });
    return () => scopeRequestsRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApparatusId]);

  useEffect(() => {
    if (!selectedApparatusId || loadedApparatusId !== selectedApparatusId) return;
    refreshHistory().catch((err: ApiError) => setError(err.message));
    return () => historyRequestsRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyStatus]);

  useEffect(
    () => () => {
      historyRequestsRef.current.cancel();
      stockLotRequestsRef.current.cancel();
    },
    []
  );

  useEffect(() => {
    setStartName(enteredName);
    setTaskName(enteredName);
    setCompleteName(enteredName);
    setAbandonName(enteredName);
    setResumeName(enteredName);
  }, [enteredName]);

  useEffect(() => {
    if (!openNotice) return;
    const timeoutId = window.setTimeout(() => setOpenNotice(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [openNotice]);

  const editorOpen = Boolean(taskModal || completeOpen || abandonOpen || resumeCheck || deleteCheck);
  useUnsavedChangesWarning(editorOpen);
  useRegisterViewRefresh(
    async () => {
      if (!selectedApparatusId) return;
      const request = scopeRequestsRef.current.begin();
      historyRequestsRef.current.cancel();
      setHistoryLoading(false);
      setBusy(true);
      setError("");
      try {
        const effectiveApparatusId = await refreshForApparatus(selectedApparatusId, request);
        if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
        setBusy(false);
        if (effectiveApparatusId === selectedApparatusIdRef.current) {
          await refreshHistoryForApparatus(effectiveApparatusId);
        }
      } catch (err: any) {
        if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
        setError(err?.message ?? "Failed to refresh truck check");
        throw err;
      } finally {
        if (isCurrentApparatusRequest(request, selectedApparatusId)) setBusy(false);
      }
    },
    {
      disabled: loading || busy || editorOpen,
      disabledReason: editorOpen ? "Finish or close the open editor before refreshing." : "Refresh is already in progress."
    }
  );

  const selectedBag = useMemo(
    () => activeCheck?.bags.find((bag) => bag.bagId === selectedBagId) ?? activeCheck?.bags[0] ?? null,
    [activeCheck, selectedBagId]
  );

  const selectedBagTaskGroups = useMemo(() => {
    if (!selectedBag) return [];
    if (!isQrsJumpKitName(selectedBag.bagName) && !isAirwayBagName(selectedBag.bagName)) {
      return [{ name: "", sortOrder: 0, items: sortRowsForRelatedDisplay(selectedBag.tasks) }];
    }
    return groupItemsForBagDisplay(selectedBag.bagName, selectedBag.tasks);
  }, [selectedBag]);

  const selectedBagVisibleTasks = useMemo(
    () => selectedBagTaskGroups.flatMap((group) => group.items),
    [selectedBagTaskGroups]
  );

  const selectedBagRelatedMetaByKey = useMemo(
    () =>
      buildRelatedRowMeta(
        selectedBagVisibleTasks.map((task) => ({
          rowKey: task.taskId,
          catalogItemId: task.catalogItemId
        }))
      ),
    [selectedBagVisibleTasks]
  );

  const openTaskModal = async (task: TruckCheckTask) => {
    if (!canModifyTruckCheckTasks(activeCheck?.check.status)) return;
    const request = stockLotRequestsRef.current.begin();
    setTaskModal(task);
    setTaskName(enteredName);
    setTaskExists(task.bagExistsChecked ?? true);
    setTaskNote(task.note ?? task.observedNotes ?? "");
    setStockLots([]);

    if (task.taskType === "item" && task.catalogItemId) {
      setStockLotsLoading(true);
      try {
        const result = await api.getStockLotsForCatalogItem(task.catalogItemId, {
          signal: request.signal,
          dedupe: false
        });
        if (!request.isCurrent()) return;
        setStockLots(result.items);
      } catch {
        if (!request.isCurrent()) return;
        setStockLots([]);
      } finally {
        if (request.isCurrent()) setStockLotsLoading(false);
      }
    } else if (request.isCurrent()) {
      setStockLotsLoading(false);
    }
  };

  const verifyItemTask = async (
    value: QuickEditValue,
    resolutionAction: "verify" | "add_expiration" | "delete" = "verify"
  ) => {
    if (!activeCheck || !taskModal) return;
    if (!canModifyTruckCheckTasks(activeCheck.check.status)) {
      setError("Completed truck checks are read-only.");
      return;
    }
    const name = value.enteredName.trim();
    if (!name) return;

    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const updated = await api.verifyTruckCheckTask(activeCheck.check.id, {
        taskId: taskModal.taskId,
        enteredName: name,
        observedQuantity: Math.max(0, Math.round(value.quantity)),
        observedExpirationDate: value.expirationDate || null,
        observedNotes: value.notes || null,
        note: resolutionAction === "delete" ? removeReasonAuditNote(value.removeReason) : value.notes || null,
        sourceType: truckCheckSource(value),
        stockLotId: value.stockLotId || null,
        stockTransferQuantity: value.stockTransferQuantity ?? null,
        resolutionAction,
        applyToInventory: true,
        checkRevision: activeCheck.check.updatedAt
      });
      closeTaskModal();
      afterTaskVerification(updated);
    } catch (err: any) {
      setError(err?.message ?? "Failed to verify task");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const submitBagTask = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeCheck || !taskModal) return;
    if (!canModifyTruckCheckTasks(activeCheck.check.status)) {
      setError("Completed truck checks are read-only.");
      return;
    }
    const name = taskName.trim();
    if (!name) return;

    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const updated = await api.verifyTruckCheckTask(activeCheck.check.id, {
        taskId: taskModal.taskId,
        enteredName: name,
        bagExistsChecked: taskExists,
        note: taskNote || null,
        resolutionAction: "verify",
        checkRevision: activeCheck.check.updatedAt
      });
      closeTaskModal();
      afterTaskVerification(updated);
    } catch (err: any) {
      setError(err?.message ?? "Failed to verify task");
    } finally {
      setBusy(false);
    }
  };

  const startCheck = async () => {
    const name = startName.trim();
    if (!selectedApparatusId || !name) return;
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const check = await api.startTruckCheck({
        apparatusId: selectedApparatusId,
        enteredName: name,
        note: startNote || null
      });
      setOpenNotice("");
      commitActiveCheck(check);
      setLoadedApparatusId(check.check.apparatusId);
      setStartNote("");
      updateViewState({
        apparatusId: check.check.apparatusId,
        checkId: check.check.id,
        bagId: selectTruckCheckBagId(check.bags, "") || null,
        historyView: false
      });
      await refreshHistoryAfterSave("Truck check opened.");
    } catch (err: any) {
      setError(err?.message ?? "Failed to start truck check");
    } finally {
      setBusy(false);
    }
  };

  const completeCheck = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeCheck) return;
    const name = completeName.trim();
    if (!name) return;
    if (!completeDate) {
      setError("Sign-off date is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const completed = await api.completeTruckCheck(activeCheck.check.id, {
        enteredName: name,
        signoffDate: completeDate || null,
        note: completeNote || null
      });
      setOpenNotice("");
      setCompleteOpen(false);
      await afterTaskUpdate(completed);
    } catch (err: any) {
      setError(err?.message ?? "Failed to complete truck check");
    } finally {
      setBusy(false);
    }
  };

  const abandonCheck = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeCheck) return;
    const name = abandonName.trim();
    if (!name) return;
    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const abandoned = await api.abandonTruckCheck(activeCheck.check.id, {
        enteredName: name,
        note: abandonNote || null
      });
      commitActiveCheck(null);
      setOpenNotice("");
      updateViewState({
        checkId: null,
        bagId: null,
        historyView: false
      });
      setAbandonOpen(false);
      setAbandonNote("");
      await refreshHistoryAfterSave("Truck check was moved to history.");
      if (abandoned.check.status !== "abandoned") {
        commitActiveCheck(abandoned);
        updateViewState({
          apparatusId: abandoned.check.apparatusId,
          checkId: abandoned.check.id,
          bagId: selectTruckCheckBagId(abandoned.bags, "") || null,
          historyView: false
        });
      }
    } catch (err: any) {
      setError(err?.message ?? "Failed to quit truck check");
    } finally {
      setBusy(false);
    }
  };

  const resumeHistoryCheck = async (id: string) => {
    const request = scopeRequestsRef.current.begin();
    historyRequestsRef.current.cancel();
    setBusy(true);
    setError("");
    try {
      const details = await api.getTruckCheck(id, {
        signal: request.signal,
        dedupe: false
      });
      if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
      commitActiveCheck(details);
      setLoadedApparatusId(details.check.apparatusId);
      updateViewState({
        apparatusId: details.check.apparatusId,
        checkId: details.check.id,
        bagId: selectTruckCheckBagId(details.bags, selectedBagId) || null,
        historyView: true
      });
      setOpenNotice(
        openedTruckCheckNotice("monthly", details.check.apparatusName, truckCheckStatusLabel(details.check.status))
      );
    } catch (err: any) {
      if (!isCurrentApparatusRequest(request, selectedApparatusId)) return;
      setError(err?.message ?? "Failed to open truck check");
    } finally {
      if (isCurrentApparatusRequest(request, selectedApparatusId)) setBusy(false);
    }
  };

  const reopenAbandonedCheck = async (event: FormEvent) => {
    event.preventDefault();
    if (!resumeCheck) return;
    const name = resumeName.trim();
    if (!name) return;

    setBusy(true);
    setError("");
    try {
      rememberName(name);
      const details = await api.reopenTruckCheck(resumeCheck.id, {
        enteredName: name,
        note: resumeNote || null
      });
      setOpenNotice("");
      commitActiveCheck(details);
      updateViewState({
        apparatusId: details.check.apparatusId,
        checkId: details.check.id,
        bagId: selectTruckCheckBagId(details.bags, selectedBagId) || null,
        historyView: false
      });
      setResumeCheck(null);
      setResumeNote("");
      await refreshHistoryAfterSave("Truck check resumed.");
    } catch (err: any) {
      setError(err?.message ?? "Failed to resume truck check");
    } finally {
      setBusy(false);
    }
  };

  const deleteAbandonedCheck = async () => {
    if (!deleteCheck) return;

    setBusy(true);
    setError("");
    try {
      await api.deleteAbandonedTruckCheck(deleteCheck.id);
      if (activeCheck?.check.id === deleteCheck.id) {
        commitActiveCheck(null);
        setOpenNotice("");
        updateViewState({
          checkId: null,
          bagId: null,
          historyView: false
        });
      }
      setDeleteCheck(null);
      await refreshHistoryAfterSave("Abandoned truck check deleted.");
    } catch (err: any) {
      setError(err?.message ?? "Failed to delete abandoned truck check");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="muted">Loading truck checks...</p>;

  const scopeReady = Boolean(selectedApparatusId) && loadedApparatusId === selectedApparatusId;
  const itemTaskModal = taskModal?.taskType === "item" ? taskModal : null;
  const bagTaskModal = taskModal?.taskType === "bag_exists" ? taskModal : null;
  const canModifyActiveCheck = scopeReady && canModifyTruckCheckTasks(activeCheck?.check.status);
  const readOnlyMessage = activeCheck ? truckCheckReadOnlyMessage(activeCheck.check.status) : "";
  const historyBusy = busy || historyLoading;

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}

      <section className="card stack-sm">
        <h2>Monthly Truck Check</h2>
        <p className="muted small">Select an apparatus, then verify each required bag and item.</p>
        <label className="stack-xs">
          <span className="label">Apparatus</span>
          <select
            className="input"
            value={selectedApparatusId}
            onChange={(event) =>
              updateViewState({
                apparatusId: event.target.value,
                checkId: null,
                bagId: null,
                historyView: false
              })
            }
            disabled={busy}
          >
            {apparatus.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      {!scopeReady ? (
        <section className="card stack-sm">
          <h3>Monthly Truck Check</h3>
          <p className="muted">
            {busy ? "Loading truck check…" : "Truck check data is unavailable. Use Refresh to try again."}
          </p>
        </section>
      ) : activeCheck ? (
        <section className="card stack-sm">
          <div className="row-between">
            <h3 className="text-wrap-anywhere">
              {viewingHistoryCheckId ? "Opened Check" : "Active Check"}: {activeCheck.check.apparatusName}
            </h3>
            <div className="actions-wrap">
              <span className={`truck-check-status ${truckCheckStatusClass(activeCheck.check.status)}`}>
                {truckCheckStatusLabel(activeCheck.check.status)}
              </span>
              <span className="small muted">
                {activeCheck.progress.verifiedTasks}/{activeCheck.progress.totalTasks}
              </span>
              {activeCheck.check.status === "in_progress" ? (
                <button className="btn btn-secondary" onClick={() => setAbandonOpen(true)} disabled={busy}>
                  Quit Check
                </button>
              ) : null}
            </div>
          </div>
          <p className="muted small">
            Check by {activeCheck.check.startedByName} · {fmt(activeCheck.check.startedAt)}
          </p>

          {openNotice ? (
            <div className="truck-check-open-banner" role="status">
              {openNotice}
            </div>
          ) : null}

          {readOnlyMessage ? (
            <div className="truck-check-readonly-banner" role="note">
              {readOnlyMessage}
            </div>
          ) : null}

          <div className="bag-grid">
            {activeCheck.bags.map((bag) => (
              <button
                key={bag.bagId}
                className="bag-tile btn-link"
                onClick={() => updateViewState({ bagId: bag.bagId })}
                style={{
                  borderColor: selectedBag?.bagId === bag.bagId ? "var(--red)" : undefined,
                  background: bag.complete ? "#f5fbf7" : "#fff"
                }}
              >
                <strong>{bag.bagName}</strong>
                <p className="small muted">
                  {bag.verifiedTasks}/{bag.totalTasks} verified
                </p>
              </button>
            ))}
          </div>

          {selectedBag ? (
            <div className="card-soft stack-sm">
              <h3>{selectedBag.bagName}</h3>
              <div className="stack-sm">
                {selectedBagTaskGroups.map((group) => (
                  <div key={group.name || selectedBag.bagId} className="kit-location-group stack-xs">
                    {group.name ? <h4 className="kit-location-heading">{group.name}</h4> : null}
                    <div className="list">
                      {group.items.map((task) => {
                        const currentlyMissing = isTruckCheckTaskCurrentlyMissing(task, selectedBag.tasks);
                        return (
                        <div key={task.taskId} className={`list-item static ${relatedRowClass(selectedBagRelatedMetaByKey[task.taskId])}`}>
                          <div className="stack-xs">
                            <div className="row gap-sm search-result-meta">
                              <strong className="text-wrap-anywhere">{task.itemName}</strong>
                              {statusChip(task, selectedBag.tasks)}
                              {currentlyMissing ? (
                                <span className="reason-chip reason-chip-yellow">Missing</span>
                              ) : null}
                              {task.alertOnly ? <span className="reason-chip reason-chip-neutral">Alert only</span> : null}
                            </div>
                            {truckCheckVerificationPrompt(task.itemName) ? (
                              <p className="small">
                                <span className="reason-label">Verify:</span> {truckCheckVerificationPrompt(task.itemName)}
                              </p>
                            ) : null}
                            {task.taskType === "item" ? (
                              <p className="muted small">
                                Required {task.requiredQuantity ?? task.expectedQuantity ?? 0} · Qty {truckCheckDisplayQuantity(task)}
                                {truckCheckDisplayExpiration(task) ? ` · Exp ${formatExpirationMonth(truckCheckDisplayExpiration(task))}` : ""}
                              </p>
                            ) : (
                              <p className="muted small">Verify kit exists</p>
                            )}
                            {task.verified ? (
                              <p className="small success-text">
                                Verified by {task.verifiedByName ?? "—"} on {fmt(task.verifiedAt)}
                              </p>
                            ) : null}
                          </div>
                          {canModifyActiveCheck ? (
                            <button className="btn btn-secondary" onClick={() => openTaskModal(task)} disabled={busy}>
                              {task.verified ? "Update" : "Verify"}
                            </button>
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeCheck.check.status === "in_progress" &&
          activeCheck.progress.totalTasks > 0 &&
          activeCheck.progress.verifiedTasks >= activeCheck.progress.totalTasks ? (
            <div className="row-end">
              <button className="btn btn-primary" onClick={() => setCompleteOpen(true)} disabled={busy}>
                Sign Off
              </button>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="card stack-sm">
          <h3>New Truck Check</h3>
          <label className="stack-xs">
            <span className="label">Your Name</span>
            <input className="input" value={startName} onChange={(event) => setStartName(event.target.value)} />
          </label>
          <label className="stack-xs">
            <span className="label">Note (optional)</span>
            <input className="input" value={startNote} onChange={(event) => setStartNote(event.target.value)} />
          </label>
          <div className="row-end">
            <button className="btn btn-primary" onClick={startCheck} disabled={busy || !selectedApparatusId || !startName.trim()}>
              {busy ? "Opening..." : "Open Check"}
            </button>
          </div>
        </section>
      )}

      {scopeReady ? (
      <section className="card stack-sm">
        <div className="row-between">
          <h3>Truck Check History</h3>
          <select
            className="input inline-control"
            value={historyStatus}
            onChange={(event) => setHistoryStatus(event.target.value as typeof historyStatus)}
            disabled={historyBusy}
          >
            <option value="all">All</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="abandoned">Abandoned</option>
          </select>
        </div>
        <div className="list">
          {history.map((row) => (
            <div key={row.id} className="list-item static">
              <div className="stack-xs">
                <strong className="text-wrap-anywhere">{row.apparatusName}</strong>
                <div className="row gap-sm search-result-meta">
                  <span className={`truck-check-status ${truckCheckStatusClass(row.status)}`}>
                    {truckCheckStatusLabel(row.status)}
                  </span>
                  <span className="muted small">
                    {row.verifiedTasks}/{row.totalTasks} verified · {row.correctedTasks ?? 0} corrected · {row.deficientTasks ?? 0} deficient · {fmt(row.startedAt)}
                  </span>
                </div>
                <p className="muted small">Signed: {row.signoffDate ?? "—"}</p>
              </div>
              <div className="actions-wrap">
                <button className="btn btn-secondary" onClick={() => resumeHistoryCheck(row.id)} disabled={historyBusy}>
                  Open
                </button>
                <a className="btn btn-secondary" href={`/api/truck-checks/${row.id}/csv`}>
                  CSV
                </a>
                {row.status === "abandoned" ? (
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setResumeCheck(row);
                      setResumeName(enteredName);
                      setResumeNote("");
                    }}
                    disabled={historyBusy}
                  >
                    Resume
                  </button>
                ) : null}
                {row.status === "abandoned" ? (
                  <button className="btn btn-danger" onClick={() => setDeleteCheck(row)} disabled={historyBusy}>
                    Delete
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          {historyLoading ? <p className="small muted">Loading truck check history…</p> : null}
          {!historyLoading && history.length === 0 ? <p className="small muted">No truck check history yet.</p> : null}
        </div>
      </section>
      ) : null}

      {itemTaskModal && canModifyActiveCheck ? (
        <QuickEditSheet
          open
          resetKey={itemTaskModal.taskId}
          title={`Verify: ${itemTaskModal.itemName}`}
          initial={initialValueForTask(itemTaskModal, enteredName)}
          presentation="modal"
          sourceMode="apparatus"
          allowedSourceTypes={["stock", "other"]}
          requireRemoveReason
          removeReasonOptions={TRUCK_CHECK_REMOVE_REASON_OPTIONS}
          allowNoChangeSave
          disableSourceUntilChange
          showMove={false}
          showNotes
          stockLots={stockLots}
          stockLotsLoading={stockLotsLoading}
          verificationPrompt={truckCheckVerificationPrompt(itemTaskModal.itemName)}
          submitLabel="Save"
          onClose={closeTaskModal}
          onSave={(value) => verifyItemTask(value, "verify")}
          onAddExpiration={(value) => verifyItemTask(value, "add_expiration")}
          onRemove={itemTaskModal.inventoryRecordId ? (value) => verifyItemTask({ ...value, quantity: 0, expirationDate: "" }, "delete") : undefined}
        />
      ) : null}

      {bagTaskModal && canModifyActiveCheck ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : closeTaskModal())}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={closeTaskModal} disabled={busy} />
            <h3>Verify: {bagTaskModal.itemName}</h3>
            {error ? <p className="error-text">{error}</p> : null}
            <form onSubmit={submitBagTask} className="stack-sm">
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={taskName} onChange={(event) => setTaskName(event.target.value)} required />
              </label>

              <label className="row gap-sm">
                <input type="checkbox" checked={taskExists} onChange={(event) => setTaskExists(event.target.checked)} />
                <span className="small">Kit exists and is present</span>
              </label>

              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={taskNote} onChange={(event) => setTaskNote(event.target.value)} />
              </label>

              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={closeTaskModal} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || !taskName.trim()}>
                  {busy ? "Saving..." : "Save Verification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {completeOpen ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setCompleteOpen(false))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setCompleteOpen(false)} disabled={busy} />
            <h3>Sign Off Truck Check</h3>
            <form onSubmit={completeCheck} className="stack-sm">
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={completeName} onChange={(event) => setCompleteName(event.target.value)} required />
              </label>
              <DateInput label="Sign-off Date" value={completeDate} onChange={setCompleteDate} required allowEmpty={false} />
              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={completeNote} onChange={(event) => setCompleteNote(event.target.value)} />
              </label>
              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={() => setCompleteOpen(false)} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || !completeName.trim()}>
                  {busy ? "Signing..." : "Sign Off"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {abandonOpen ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setAbandonOpen(false))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setAbandonOpen(false)} disabled={busy} />
            <h3>Quit Truck Check?</h3>
            <p className="muted small">
              This will stop the active check and move it to abandoned history. Inventory changes already made during the check will remain.
            </p>
            <form onSubmit={abandonCheck} className="stack-sm">
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={abandonName} onChange={(event) => setAbandonName(event.target.value)} required />
              </label>
              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={abandonNote} onChange={(event) => setAbandonNote(event.target.value)} />
              </label>
              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={() => setAbandonOpen(false)} disabled={busy}>
                  Keep Checking
                </button>
                <button type="submit" className="btn btn-danger" disabled={busy || !abandonName.trim()}>
                  {busy ? "Quitting..." : "Quit Check"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {resumeCheck ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setResumeCheck(null))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setResumeCheck(null)} disabled={busy} />
            <h3>Resume Abandoned Check?</h3>
            <p className="muted small">
              This will move the abandoned check back to In Progress and reload its checklist from the current apparatus
              inventory. Existing inventory will not be reset.
            </p>
            <form onSubmit={reopenAbandonedCheck} className="stack-sm">
              <div className="card-soft stack-xs">
                <strong>{resumeCheck.apparatusName}</strong>
                <p className="muted small">
                  {truckCheckStatusLabel(resumeCheck.status)} · {fmt(resumeCheck.startedAt)}
                </p>
              </div>
              <label className="stack-xs">
                <span className="label">Your Name</span>
                <input className="input" value={resumeName} onChange={(event) => setResumeName(event.target.value)} required />
              </label>
              <label className="stack-xs">
                <span className="label">Note (optional)</span>
                <input className="input" value={resumeNote} onChange={(event) => setResumeNote(event.target.value)} />
              </label>
              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={() => setResumeCheck(null)} disabled={busy}>
                  Keep Abandoned
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || !resumeName.trim()}>
                  {busy ? "Resuming..." : "Resume Check"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteCheck ? (
        <div className="modal-backdrop" onClick={() => (busy ? undefined : setDeleteCheck(null))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setDeleteCheck(null)} disabled={busy} />
            <h3>Delete Abandoned Check?</h3>
            <p className="muted small">
              This removes the abandoned truck check report from history. Inventory changes already made during the check
              will remain.
            </p>
            <div className="card-soft stack-xs">
              <strong>{deleteCheck.apparatusName}</strong>
              <p className="muted small">
                {truckCheckStatusLabel(deleteCheck.status)} · {fmt(deleteCheck.startedAt)}
              </p>
            </div>
            <div className="actions-wrap">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteCheck(null)} disabled={busy}>
                Keep Check
              </button>
              <button type="button" className="btn btn-danger" onClick={deleteAbandonedCheck} disabled={busy}>
                {busy ? "Deleting..." : "Delete Check"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
